import { useState } from 'react';
import { sprintf } from 'sprintf-js';

import { Box } from 'components/Box';
import { Button } from 'components/Button/Button';
import { Hide } from 'components/Hide';
import { TrackedLink } from 'components/Link';
import { IdealistLogo } from 'components/Logo/IdealistLogo';
import { H4 } from 'components/Text/H4';
import { useUserEnvironment } from 'modules/main/store/hooks/useUserEnvironment';
import { userDashboardSavedItemsRoute } from 'modules/userDashboard/userDashboard.routes';
import { colors, gutterWidth } from 'theme/theme';
import { hasResumeDb } from 'utils/resumeDatabase';

import { StaffPageHeaderSearchMenu } from './Search/StaffPageHeaderSearchMenu';
import { StaffMobileNotificationsMenu } from './StaffMobileNotificationsMenu';
import { StaffPageHeaderAccountDropdown } from './StaffPageHeaderAccountDropdown';
import { STAFF_PAGE_HEADER_NAV } from './StaffPageHeaderConstants';
import { StaffPageHeaderHolder } from './StaffPageHeaderHolder';
import { StaffPageHeaderMainLink } from './StaffPageHeaderMainLink';
import { StaffPageHeaderMobileSectionTitle } from './StaffPageHeaderMobileSectionTitle';
import { StaffPageHeaderNavLink } from './StaffPageHeaderNavLink';
import { StaffPageHeaderNavLinks } from './StaffPageHeaderNavLinks';
import { StaffPageHeaderNotificationsDropdown } from './StaffPageHeaderNotificationsDropdown';
import { StaffPageHeaderSeparator } from './StaffPageHeaderSeparator';

export function StaffPageHeader() {
  const userEnvironment = useUserEnvironment();
  const { user } = userEnvironment;

  const [menuOpen, setMenuOpen] = useState(false);

  const navOptions = STAFF_PAGE_HEADER_NAV;

  const [searchOpen, setSearchOpen] = useState(false);

  const toggleSearch = (newSearchOpen: boolean = !searchOpen) => {
    setSearchOpen(newSearchOpen);
  };

  return (
    <StaffPageHeaderHolder>
      <Box
        classes={['flex', 'align-center', 'justify-between']}
        position={['fixed', null, null, 'relative']}
        top={0}
        left={0}
        width={1}
        height={[60, null, null, 40]}
        px={gutterWidth / 2}
        bg="white"
        boxShadow={['0 2px 4px rgba(0,0,0,0.075)', null, null, 'none']}
        pointerEvents="auto"
      >
        <Box mr={24} flex="0 0 auto">
          <TrackedLink
            href="/staff"
            title="Idealist Staff"
            eventName="Idealist Logo Header"
            data-qa-id="header-logo-link"
          >
            <div style={{ lineHeight: 0 }}>
              <IdealistLogo withText withStaffMarker height={28} />
            </div>
          </TrackedLink>
        </Box>

        <Hide lg xl>
          <Box ml="auto" classes={['flex', 'align-center']}>
            <div>
              <Button
                onClick={() => toggleSearch()}
                aria-label={getText('Toggle search')}
                title={getText('Toggle search')}
                data-qa-id="search-menu-button"
                variant="tertiary-grey"
                icon={{ type: 'normalized', name: 'search' }}
              />
            </div>

            <StaffMobileNotificationsMenu />

            <div style={{ marginLeft: 4 }}>
              <Button
                onClick={() => setMenuOpen(!menuOpen)}
                aria-label={getText('Toggle menu')}
                title={getText('Toggle menu')}
                data-qa-id="mobile-menu-button"
                variant="tertiary-grey"
                icon={{ type: 'normalized', name: 'hamburger' }}
              />
            </div>
          </Box>
        </Hide>

        <StaffPageHeaderNavLinks
          flex="1 1 auto"
          $menuOpen={menuOpen}
          onClick={() => setMenuOpen(false)}
          height={[null, null, null, '100%']}
        >
          <Hide lg xl height={[null, null, null, '100%']}>
            <Box
              classes={['flex', 'align-center', 'justify-between']}
              px={gutterWidth / 2}
              height={60}
              width="100%"
              borderBottom={`1px solid ${colors.selectionGrey}`}
            >
              <H4 mb={0} color={colors.brandBlue}>
                {
                  /* TRANSLATORS: user greeting at top of page */
                  sprintf(getText('Hi, %(name)s!'), {
                    name: user ? user.firstName || user.email : '',
                  })
                }
              </H4>

              <div style={{ margin: '0 auto' }}>
                <Button
                  onClick={() => setMenuOpen(false)}
                  aria-label={getText('Close search')}
                  variant="tertiary-grey"
                  icon={{ type: 'normalized', name: 'close' }}
                />
              </div>
            </Box>
          </Hide>

          <Box
            display={['block', null, null, 'flex']}
            height={[null, null, null, '100%']}
            justifyContent="space-between"
            p={[gutterWidth / 2, null, null, 0]}
            pb={[0, null, null, 0]}
            data-qa-id="nav-dropdown-mobile"
          >
            <Box
              display={['block', null, null, 'flex']}
              alignItems="center"
              mt="1px"
            >
              {navOptions.map((navOption, index) => (
                <StaffPageHeaderMainLink
                  key={navOption}
                  index={index}
                  navOption={navOption}
                />
              ))}
            </Box>

            <Box display={['block', null, null, 'flex']} alignItems="center">
              <Hide lg xl>
                <StaffPageHeaderMobileSectionTitle>
                  {getText('My Account')}
                </StaffPageHeaderMobileSectionTitle>

                <StaffPageHeaderNavLink
                  type="USER_DASHBOARD"
                  to={userDashboardSavedItemsRoute.with({})}
                >
                  {getText('My Saved Items')}
                </StaffPageHeaderNavLink>

                {hasResumeDb(userEnvironment) && (
                  <StaffPageHeaderNavLink
                    type="USER_DASHBOARD"
                    to="/dashboard/employment-profile"
                  >
                    {getText('Profile')}
                  </StaffPageHeaderNavLink>
                )}

                <StaffPageHeaderNavLink
                  data-qa-id="page-header-account-dropdown-account-settings"
                  type="USER_DASHBOARD"
                  to="/dashboard/account-settings"
                >
                  {getText('Settings')}
                </StaffPageHeaderNavLink>

                {user && user.orgs.length > 0 && (
                  <>
                    <StaffPageHeaderMobileSectionTitle>
                      {getText('Manage')}
                    </StaffPageHeaderMobileSectionTitle>

                    {user.orgs.map((org) => (
                      <Box key={org.id}>
                        <StaffPageHeaderNavLink
                          type="ORG_DASHBOARD"
                          to={
                            org.hasGradProgram
                              ? `/admin/${org.id}/grad-program`
                              : `/admin/${org.id}`
                          }
                        >
                          {org.name}
                        </StaffPageHeaderNavLink>
                      </Box>
                    ))}
                  </>
                )}
              </Hide>

              <Box
                display={['none', 'none', 'none', 'flex']}
                alignItems="center"
              >
                <StaffPageHeaderAccountDropdown />
                <StaffPageHeaderSeparator />
              </Box>

              <Box
                display={['none', 'none', 'none', 'flex']}
                alignItems="center"
              >
                <StaffPageHeaderNotificationsDropdown />
              </Box>
            </Box>
          </Box>
        </StaffPageHeaderNavLinks>
      </Box>

      <StaffPageHeaderSearchMenu
        searchOpen={searchOpen}
        toggleSearch={toggleSearch}
      />
    </StaffPageHeaderHolder>
  );
}
